import React, { useState } from 'react';
import memoize from 'memoize-one';
import snakeCaseKeys from 'snakecase-keys';

import { useDispatch } from 'react-redux';
import { useAbility } from '@casl/react';
import {
  indexPermissionClassificationsRequest,
  importPermissionClassificationRequest,
  preImportPermissionClassificationRequest,
  exportPermissionClassificationsRequest,
  importTemplatePermissionClassificationRequest,
  updatePermissionClassificationRequest,
  deletePermissionClassificationRequest,
  activePermissionClassificationRequest,
  deactivatePermissionClassificationRequest
} from '../../requests/permissionClassifications';
import { downloadFile, camelCaseEmptyStringRecursive } from '../../services/utils';
import { sendAlert } from '../../actions/utils';
import {
  ActiveDot,
  ComponentDataTable,
  DefaultModal,
  SimpleCenteredModal,
  ImportActions,
  ImportModal,
  SimpleShow,
  SettingsMassiveActions,
  BasicActionBtns
} from '../../components';

import PermissionClassificationForm from './PermissionClassificationForm';
import { AbilityContext } from '../../config/abilityContext';

const PERMISSION_TYPE_LABELS = {
  with_pay: 'Permiso con Goce',
  without_pay: 'Permiso sin Goce'
};

const permissionTypeLabel = value => PERMISSION_TYPE_LABELS[value] || value || '';

const columns = memoize(clickHandler => [
  {
    name: 'NOMBRE',
    selector: 'name',
    sortable: true,
    grow: '1'
  },
  {
    name: 'TIPO DE PERMISO',
    selector: 'permission_type',
    cell: item => permissionTypeLabel(item.permissionType || item.permission_type),
    sortable: true,
    grow: '1'
  },
  {
    name: 'ACTIVO',
    selector: 'active',
    cell: item => <ActiveDot item={item} />,
    sortable: true,
    grow: '1',
    center: true
  },
  {
    name: 'ACCIONES',
    cell: item => (
      <BasicActionBtns
        item={item}
        withArchive={false}
        resource="PermissionClassification"
        clickHandler={clickHandler}
      />
    ),
    ignoreRowClick: true,
    allowOverflow: false,
    button: true,
    grow: '1',
    minWidth: '123px'
  }
]);

const PermissionClassificationDataTable = ({ moreData, setMoreData }) => {
  const ability = useAbility(AbilityContext);
  const [amount, setAmount] = useState(0);
  const [modalAction, setModalAction] = useState(() => null);
  const [modalBody, setModalBody] = useState('');
  const [modalItem, setModalItem] = useState({});
  const [modalShow, setModalShow] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [simpleModalBody, setSimpleModalBody] = useState('');
  const [simpleModalShow, setSimpleModalShow] = useState(false);
  const [simpleModalTitle, setSimpleModalTitle] = useState('');
  const [onRequest, setOnRequest] = useState(true);
  const [permissionClassifications, setPermissionClassifications] = useState([]);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedCount, setSelectedCount] = useState('');
  const [query, setQuery] = useState({});

  const dispatch = useDispatch();

  const handleSelectedRows = item => {
    setSelectedRows(item.selectedRows);
    setSelectedCount(item.selectedCount);
  };

  const handleModalClose = () => {
    setModalShow(false);
  };

  const handleSuccessIndex = response => {
    const { data, metadata } = response.data;
    setPermissionClassifications(data);
    setAmount(metadata.amount);
    setOnRequest(false);
  };

  const handleFailureRequest = error => {
    const { response } = error;
    handleModalClose();
    setOnRequest(false);
    dispatch(sendAlert({ kind: 'error', message: response?.data?.message }));
  };

  const handleSuccessRemove = () => {
    dispatch(sendAlert({ kind: 'success', message: 'Clasificación de permiso eliminada correctamente' }));
    handleModalClose();
    setOnRequest(false);
    setMoreData(!moreData);
  };

  const removePermissionClassification = item => {
    setOnRequest(true);
    deletePermissionClassificationRequest({
      params: { ids: item.id },
      dispatch,
      successCallback: handleSuccessRemove,
      failureCallback: handleFailureRequest,
      callback: () => {
        handleModalClose();
        setOnRequest(false);
      }
    });
  };

  const handleRequest = async params => {
    setOnRequest(true);
    setQuery(params);
    indexPermissionClassificationsRequest({
      dispatch,
      params,
      successCallback: handleSuccessIndex
    });
  };

  const handleUpdate = permissionClassificationValue => {
    const {
      permissionClassification: { id: permissionClassificationId }
    } = permissionClassificationValue;
    updatePermissionClassificationRequest(permissionClassificationId, {
      dispatch,
      params: snakeCaseKeys(permissionClassificationValue),
      formData: true,
      successCallback: () => {
        dispatch(sendAlert({ kind: 'success', message: 'Clasificación de permiso actualizada con éxito' }));
        setSimpleModalShow(false);
        setMoreData(!moreData);
      }
    });
  };

  const handleSuccessAction = message => {
    dispatch(sendAlert({ kind: 'success', message }));
    handleModalClose();
    setMoreData(!moreData);
  };

  const handleButtonClick = (item, action) => {
    switch (action) {
      case 'show':
        setSimpleModalShow(true);
        setSimpleModalTitle('Clasificación de permiso');
        setSimpleModalBody(
          <SimpleShow
            item={camelCaseEmptyStringRecursive(item)}
            handleClose={() => setSimpleModalShow(false)}
            withActive
            customFields={[
              {
                name: 'Tipo de permiso',
                value: item.parsed_permission_type
              }
            ]}
          />
        );
        break;
      case 'edit':
        if (ability.can('update', 'PermissionClassification')) {
          setSimpleModalShow(true);
          setSimpleModalTitle('Editar clasificación de permiso');
          setSimpleModalBody(
            <PermissionClassificationForm
              permissionClassification={camelCaseEmptyStringRecursive(item)}
              action="edit"
              formRequest={handleUpdate}
            />
          );
        }
        break;

      case 'destroy':
        setModalTitle('Eliminar clasificación de permiso');
        setModalShow(true);
        setModalBody('¿Estás seguro que deseas eliminar esta clasificación de permiso?');
        setModalItem(item);
        setModalAction(() => removePermissionClassification);
        break;
      default:
        // eslint-disable-next-line no-console
        console.log('Error: Action not found');
    }
  };

  const sortColumnCase = name => ({ sort_column: name });

  const handleExportRequest = () => {
    setOnRequest(true);
    exportPermissionClassificationsRequest({
      dispatch,
      params: query,
      successCallback: response => {
        downloadFile(response);
        setOnRequest(false);
        setModalShow(false);
      },
      failureCallback: handleFailureRequest
    });
  };

  const massiveDeletePermissionClassification = () => {
    const permissionClassificationSelected = selectedRows.map(item => item.id);
    setOnRequest(true);
    deletePermissionClassificationRequest({
      params: { ids: permissionClassificationSelected },
      dispatch,
      successCallback: () => handleSuccessAction('Clasificaciones de permiso eliminadas correctamente'),
      failureCallback: handleFailureRequest,
      callback: () => {
        handleModalClose();
        setOnRequest(false);
        setClearSelectedRows(!clearSelectedRows);
      }
    });
  };

  const massiveActivePermissionClassification = () => {
    const selected = selectedRows.map(item => item.id);
    setOnRequest(true);
    activePermissionClassificationRequest({
      params: { ids: selected },
      dispatch,
      successCallback: () => handleSuccessAction('Clasificaciones de permiso activadas correctamente'),
      callback: () => {
        handleModalClose();
        setOnRequest(false);
        setClearSelectedRows(!clearSelectedRows);
      }
    });
  };

  const massiveDeactivatePermissionClassification = () => {
    const selected = selectedRows.map(item => item.id);
    setOnRequest(true);
    deactivatePermissionClassificationRequest({
      params: { ids: selected },
      dispatch,
      successCallback: () => handleSuccessAction('Clasificaciones de permiso desactivadas correctamente'),
      callback: () => {
        handleModalClose();
        setOnRequest(false);
        setClearSelectedRows(!clearSelectedRows);
      }
    });
  };

  const handleImportModalExceptions = error => {
    if (error?.response?.status === 422) {
      const alertInfo = <pre>{error?.response?.data?.message}</pre>;
      setModalTitle('Información Relevante');
      setModalShow(true);
      setModalBody(alertInfo);
      setModalAction(() => handleModalClose);
    } else {
      dispatch(sendAlert({ kind: 'error', message: error?.response?.data?.message }));
    }
  };

  const handleMassAction = action => {
    switch (action) {
      case 'import':
        setSimpleModalTitle('Importar clasificaciones de permiso');
        setSimpleModalBody(
          <ImportModal
            onDropUploaded={preImportPermissionClassificationRequest}
            handleTemplate={importTemplatePermissionClassificationRequest}
            hideModal={() => setSimpleModalShow(false)}
            onHide={importPermissionClassificationRequest}
            updateData={() => setMoreData(!moreData)}
            handleExceptions={handleImportModalExceptions}
          />
        );
        setSimpleModalShow(true);
        break;
      case 'export':
        setModalTitle('Exportar clasificaciones de permiso');
        setModalShow(true);
        setModalBody('¿Estás seguro que deseas exportar clasificaciones de permiso?');
        setModalAction(() => handleExportRequest);
        break;
      case 'massive_destroy':
        setModalTitle('Eliminar clasificaciones de permiso');
        setModalShow(true);
        setModalBody('¿Estás seguro que deseas eliminar las clasificaciones de permiso seleccionadas?');
        setModalAction(() => massiveDeletePermissionClassification);
        break;
      case 'massive_active':
        setModalTitle('Activar clasificaciones de permiso');
        setModalShow(true);
        setModalBody('¿Estás seguro que deseas activar las clasificaciones de permiso seleccionadas?');
        setModalAction(() => massiveActivePermissionClassification);
        break;
      case 'massive_deactivate':
        setModalTitle('Desactivar clasificaciones de permiso');
        setModalShow(true);
        setModalBody('¿Estás seguro que deseas desactivar las clasificaciones de permiso seleccionadas?');
        setModalAction(() => massiveDeactivatePermissionClassification);
        break;
      default:
        // eslint-disable-next-line no-console
        console.log('Error: Action not found');
    }
  };

  return (
    <>
      <ComponentDataTable
        onRequest={onRequest}
        columns={columns(handleButtonClick)}
        handleSortCase={sortColumnCase}
        data={permissionClassifications}
        totalRows={amount}
        moreData={moreData}
        selectableRows
        withMassActions
        massActions={
          <>
            <ImportActions model="PermissionClassification" handleClick={handleMassAction} />
            <SettingsMassiveActions
              model="PermissionClassification"
              handleClick={handleMassAction}
              disabled={!selectedCount}
            />
          </>
        }
        resourceRequest={handleRequest}
        onRowClicked={item => handleButtonClick(item, 'show')}
        pointerOnHover={ability.can('update', 'PermissionClassification')}
        preName="permission_classifications"
        clearSelectedRows={clearSelectedRows}
        onSelectedRowsChange={handleSelectedRows}
      />
      <DefaultModal
        title={modalTitle}
        body={modalBody}
        show={modalShow}
        handleClose={handleModalClose}
        handleConfirm={() => modalAction(modalItem)}
        disabled={onRequest}
        variantBtnClose="outline-info"
        variantBtnSave="primary"
      />
      <SimpleCenteredModal
        title={simpleModalTitle}
        body={simpleModalBody}
        show={simpleModalShow}
        onHide={() => setSimpleModalShow(false)}
      />
    </>
  );
};

export default PermissionClassificationDataTable;
